import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { Button } from '@repo/ui';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useSelector } from 'react-redux';

import { DrawerPopup } from 'components/UI/DrawerPopup';
import { useSendMagicAuthCode } from 'hooks/api/useWorkOSAuth';
import {
  ContactDetailsFields,
  isContactDetailsValid,
} from 'components/ContactDetailsFields';
import { GoogleLoginButton } from 'components/GoogleLoginButton/GoogleLoginButton';
import { OnboardingAuthPanel } from 'pages/onboarding/components/OnboardingAuthPanel';
import { createApiCall } from 'services';
import { requestPositionForAccess } from 'utils/accessConsent';
import {
  clearClaimIntent,
  consumeClaimIntent,
  peekClaimIntent,
  rememberClaimIntent,
  rememberReturnPath,
} from 'utils/postAuthReturn';
import {
  claimFailureReason,
  claimOnScan,
  mintClaimTicket,
} from 'hooks/api/claimOnScan';
import type { ClaimFailureReason } from 'hooks/api/claimOnScan';
import { useGateRendersOwnTitle, useGateSurfaceSx } from './gatePresentation';

/**
 * VRG-720 slice 2 -- "Claim ownership", the replacement for the access form on an item whose
 * product has claim on scan switched on and which nobody has claimed yet (a Vera business
 * card is the live case).
 *
 * ## What the visitor does
 *
 * Tap the card, see the page, press Claim ownership. ONE drawer then walks them through
 * steps in place (working-conventions section 52: never a second or third sheet):
 *
 *   1. details   name + email (or, if already signed in, a statement of whose account it
 *                goes to), Continue with Google, and a location prompt when the product
 *                collects one;
 *   2. otp       verify the email with a one-time code (skipped for Google and for an
 *                already signed-in visitor, whose identity is already proven);
 *   3. working   the claim itself.
 *
 * ## What authorises the claim
 *
 * The ticket, not this component. It is minted from the fresh-tap `vt` in the URL when the
 * page loads, so a link that was copied (no `vt`) or is stale never gets one and the card
 * says to tap again instead of offering a button that cannot work. The server re-checks the
 * ticket, the signed-in session and that the item is still unclaimed.
 *
 * ## A visitor who is already signed in as someone else
 *
 * The email they might type is never used as an identity. A signed-in visitor is not asked
 * for one at all: the drawer says "This item will be added to <their email>" and claims as
 * that account. That is the safer of the two options (the other being to sign them out
 * first): nobody can end up owning the item under an address they did not just prove, and
 * nobody is logged out of something they were using.
 */

type Step = 'details' | 'otp' | 'working' | 'failed';

/*
 * VRG-818 — the copy says "it", not "the card".
 *
 * This feature was built for Vera's own business cards and the noun was written for
 * that one product ("Tap the card to claim it"). It is not card-only: the DAC Art
 * Basel capsule is a sphere with a tag inside, so the page read "Tap the card to
 * claim it" under a photograph of a ball.
 *
 * "Tag" is the house word for the hardware and is the wrong substitution HERE: the
 * tag is the inlay, which a business-card recipient has never seen and has no name
 * for. Naming the product per record would be better still, but that is a field to
 * author and a decision about who authors it (VRG-818 option B) — this is the
 * correction that is true of every product today.
 *
 * ⚠️ Do not "restore" a product noun to one of these strings without making it come
 * from the product. A literal is right for exactly one SKU and wrong for the rest.
 */
const FAILURE_COPY: Record<
  Exclude<ClaimFailureReason, 'vt_unverified' | 'ticket_invalid'>,
  { title: string; text: string }
> = {
  ticket_expired: {
    title: 'Tap it again',
    text: 'This claim timed out. Tap it again with your phone.',
  },
  already_claimed: {
    title: 'Already claimed',
    text: 'Someone has already claimed this item.',
  },
  not_claimable: {
    title: 'This item cannot be claimed',
    text: 'It is no longer available to claim.',
  },
  own_item: {
    title: 'This is already yours',
    text: 'You already own this item.',
  },
  unauthenticated: {
    title: 'We could not confirm your sign in',
    text: 'Nothing was changed. Go back and try again.',
  },
  unknown: {
    title: 'Something went wrong',
    text: 'Nothing was changed. Try again, or tap it again.',
  },
};

const failureCopy = (reason: ClaimFailureReason) =>
  FAILURE_COPY[
    reason === 'vt_unverified' || reason === 'ticket_invalid' ? 'ticket_expired' : reason
  ];

const splitName = (full: string) => {
  const trimmed = full.trim();
  const at = trimmed.indexOf(' ');
  return at === -1
    ? { firstName: trimmed, lastName: '' }
    : { firstName: trimmed.slice(0, at), lastName: trimmed.slice(at + 1).trim() };
};

export const ClaimOnScanCard = ({
  certificateId,
  vt,
  collectEmail,
  collectLocation,
  ownerName,
  companyId,
  onDismiss,
}: {
  certificateId: string;
  /**
   * The certificate's brand. An owner sign-in by email code is scoped to a company, and the
   * verify call refuses without one ("Company context is required for owner authentication"),
   * so without this the signed-out claim could send a code but never accept it.
   */
  companyId?: string | null;
  /**
   * The current (issuing) owner's display name, when the API released it. For an unclaimed
   * claim-on-scan item it does, so a business card says whose card it is.
   */
  ownerName?: string | null;
  /** The fresh-tap token from the URL, if any. */
  vt: string | null;
  collectEmail: boolean;
  collectLocation: boolean;
  /** "View this item instead" -- hands the page back to the ordinary access flow. */
  onDismiss: () => void;
}) => {
  // VRG-720 comparison harness — `card` (in-flow) or `sheet`. See gatePresentation.
  const gateSurfaceSx = useGateSurfaceSx();
  const gateRendersOwnTitle = useGateRendersOwnTitle();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const profile = useSelector(
    ({ auth }: { auth: { profile: any } }) => auth.profile,
  ) as {
    email?: string;
    first_name?: string;
    last_name?: string;
  } | null;
  const signedInEmail = profile?.email?.trim() || '';

  // ---- the ticket -------------------------------------------------------------------
  const [ticket, setTicket] = useState<string | null>(
    () => peekClaimIntent(certificateId)?.ticket ?? null,
  );
  const [ticketState, setTicketState] = useState<'idle' | 'minting' | 'ready' | 'none'>(
    () => (peekClaimIntent(certificateId) ? 'ready' : vt ? 'minting' : 'none'),
  );
  const mintStarted = useRef(false);

  useEffect(() => {
    if (ticket || !vt || mintStarted.current) return;
    mintStarted.current = true;
    setTicketState('minting');
    mintClaimTicket(certificateId, vt)
      .then(result => {
        setTicket(result.ticket);
        setTicketState('ready');
      })
      .catch(error => {
        // 409 here is the item having been claimed between the page load and now; the
        // others mean the tap was not fresh. Either way there is no ticket, and the card
        // falls back to the "tap to claim" copy.
        const reason = claimFailureReason(error);
        if (reason === 'already_claimed' || reason === 'not_claimable') {
          void queryClient.invalidateQueries({ queryKey: ['certificate-public', certificateId] });
        }
        setTicketState('none');
      });
  }, [certificateId, vt, ticket, queryClient]);

  // ---- the drawer -------------------------------------------------------------------
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>('details');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ClaimFailureReason | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  // The position taken on the Continue tap, carried through the code step.
  const locationRef = useRef<{ latitude: number; longitude: number } | undefined>(undefined);

  /**
   * Best effort, and NEVER a gate — "capture if granted" (Spencer, 2026-10-29).
   *
   * The ACCESS gate still requires a position when the brand collects one, and that
   * asymmetry is the decision rather than an oversight. Refusing to show a stranger a
   * certificate until they share where they are is the brand's call, and a visitor who
   * will not share can walk away. A CLAIM is not that: the item is already theirs, the
   * tap proved they are holding it, and a browser answering `denied` — which it may have
   * been told to do months ago, on a different site, leaving no prompt to re-offer —
   * would otherwise leave them unable to take ownership at all. Terminal for them, and
   * invisible to the brand. So a position is recorded when the device offers one, and
   * skipped when it does not.
   *
   * `requestPositionForAccess` settles under its own hard cap, so this can never be the
   * reason a claim hangs.
   */
  const acquireLocation = async (): Promise<void> => {
    locationRef.current = undefined;
    if (!collectLocation) return;
    const position = await requestPositionForAccess();
    if (!position.ok) return;
    locationRef.current = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
    };
  };

  /** A position was actually captured — not merely asked for. */
  const haveLocation = () => collectLocation && locationRef.current !== undefined;

  /** Best effort: the brand's access log must never be the reason a claim fails. */
  const logAccess = async (who: { name?: string; email?: string }) => {
    try {
      await createApiCall({
        method: 'POST',
        url: `certificates/public/${certificateId}/access`,
        data: {
          ...(collectEmail && who.email ? { email: who.email } : {}),
          ...(collectEmail && who.email && who.name ? { name: who.name } : {}),
          // `consent` is what THIS visitor agreed to, not what the brand asked for. With
          // no position there was no agreement, and the server stores the pair as given
          // (`log-public-access`: `effectiveConsent = locationAllowed && consent`).
          consent: haveLocation(),
          location: haveLocation() ? locationRef.current : undefined,
        },
      });
    } catch (error) {
      console.warn('Could not log access for the claim:', error);
    }
  };

  const claimNow = async (activeTicket: string) => {
    setStep('working');
    setBusy(true);
    setFailure(null);
    try {
      await claimOnScan(certificateId, activeTicket);
      // The page re-reads itself as the new owner; this card unmounts when it does.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['certificate-public', certificateId] }),
        queryClient.invalidateQueries({ queryKey: ['certificates'] }),
      ]);
      setOpen(false);
    } catch (error) {
      const reason = claimFailureReason(error);
      if (reason === 'ticket_expired' || reason === 'ticket_invalid' || reason === 'vt_unverified') {
        setTicket(null);
        setTicketState('none');
      }
      if (reason === 'already_claimed' || reason === 'own_item' || reason === 'not_claimable') {
        void queryClient.invalidateQueries({ queryKey: ['certificate-public', certificateId] });
      }
      setFailure(reason);
      setStep('failed');
    } finally {
      setBusy(false);
    }
  };

  // ---- coming back from Google ------------------------------------------------------
  const resumed = useRef(false);
  const [awaitingSignIn, setAwaitingSignIn] = useState(
    () => peekClaimIntent(certificateId) !== null,
  );

  useEffect(() => {
    if (!awaitingSignIn) return;
    if (profile?.email) {
      if (resumed.current) return;
      const intent = consumeClaimIntent(certificateId);
      setAwaitingSignIn(false);
      if (!intent) return;
      resumed.current = true;
      locationRef.current = intent.location;
      setTicket(intent.ticket);
      setTicketState('ready');
      setOpen(true);
      void (async () => {
        await logAccess({
          name: `${profile.first_name ?? ''} ${profile.last_name ?? ''}`.trim(),
          email: profile.email,
        });
        await claimNow(intent.ticket);
      })();
      return;
    }
    // The profile arrives a beat after the redirect lands. If it never does the sign in
    // failed: drop the intent and let the visitor press Claim again (the ticket is kept).
    const timeout = window.setTimeout(() => {
      clearClaimIntent();
      setAwaitingSignIn(false);
    }, 10000);
    return () => window.clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitingSignIn, profile?.email]);

  // ---- handlers ---------------------------------------------------------------------
  const openDrawer = () => {
    setFailure(null);
    setStep('details');
    setPinnedHeight(null);
    setOpen(true);
  };

  /*
    §52 — the sheet must not change height between steps (details -> code -> working ->
    done/failed), or it reads as jumpy. It still sizes to its content, so the FIRST step's
    measured height becomes the floor for every later step: shorter steps keep the footprint,
    a taller one may still grow. Re-measured each time the drawer opens.
  */
  const drawerBodyRef = useRef<HTMLDivElement>(null);
  const [pinnedHeight, setPinnedHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (!open || pinnedHeight !== null || !drawerBodyRef.current) return;
    const height = drawerBodyRef.current.offsetHeight;
    if (height > 0) setPinnedHeight(height);
  });

  const closeDrawer = () => {
    if (busy) return;
    setOpen(false);
  };

  /** Signed-in visitor: claim as the account they are already using. */
  const handleClaimSignedIn = async () => {
    if (!ticket || busy) return;
    setBusy(true);
    await acquireLocation();
    await logAccess({
      name: `${profile?.first_name ?? ''} ${profile?.last_name ?? ''}`.trim(),
      email: signedInEmail,
    });
    await claimNow(ticket);
  };

  /** Signed-out visitor: details -> log access -> email code. */
  const sendMagicAuthCode = useSendMagicAuthCode();
  const [codeSent, setCodeSent] = useState(false);

  const handleContinue = async () => {
    if (busy) return;
    // Safari autofill can fill a field without telling React; read the DOM as a fallback.
    const currentName = name || nameInputRef.current?.value || '';
    const currentEmail = email || emailInputRef.current?.value || '';
    if (!isContactDetailsValid(currentName, currentEmail)) {
      setShowErrors(true);
      return;
    }
    setBusy(true);
    await acquireLocation();
    setName(currentName.trim());
    setEmail(currentEmail.trim());
    await logAccess({ name: currentName.trim(), email: currentEmail.trim() });
    // Send the code as part of Continue, so the next screen is code entry rather than a
    // second "Send Code" tap for something the visitor already asked for. If sending fails,
    // the panel opens on its own Send Code screen, which owns the retry and the error copy.
    try {
      await sendMagicAuthCode.mutateAsync({
        email: currentEmail.trim(),
        companyId: companyId ?? undefined,
      });
      setCodeSent(true);
    } catch {
      setCodeSent(false);
    }
    setBusy(false);
    setStep('otp');
  };

  const handleGoogle = async (): Promise<boolean> => {
    if (!ticket) return false;
    setBusy(true);
    await acquireLocation();
    rememberClaimIntent({
      certificateId,
      ticket,
      locationConsent: haveLocation(),
      location: locationRef.current,
    });
    // The certificate's own path: the `vt` has done its job (the ticket carries the proof),
    // and `window.location` cannot be trusted here because a signed-out visitor's 401s push
    // `/sign-in` into the address bar without re-rendering (services/index.ts `logOut`).
    rememberReturnPath(`/certificates/${certificateId}`);
    // Left busy: the page is about to navigate away.
    return true;
  };

  // ---- render -----------------------------------------------------------------------
  const noTicket = ticketState === 'none';
  const minting = ticketState === 'minting' || awaitingSignIn;

  const drawerBody = (() => {
    if (step === 'working') {
      return (
        <Stack spacing={1.5} alignItems='center' sx={{ py: 4 }}>
          <Loader2 className='w-6 h-6 animate-spin' />
          <Typography variant='body2' color='text.secondary'>
            Adding this item to your account
          </Typography>
        </Stack>
      );
    }

    if (step === 'failed' && failure) {
      const copy = failureCopy(failure);
      const goBack = failure === 'unauthenticated' || failure === 'unknown';
      return (
        <Stack spacing={1.5}>
          <Typography variant='h6' sx={{ fontSize: 20, fontWeight: 500 }}>
            {copy.title}
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.5 }}>
            {copy.text}
          </Typography>
          {goBack ? (
            <Button
              variant='primary'
              className='w-full h-10'
              sx={{ paddingX: 2 }}
              onClick={() => {
                setFailure(null);
                setStep('details');
              }}
            >
              Try again
            </Button>
          ) : (
            <Button
              variant='primary'
              className='w-full h-10'
              sx={{ paddingX: 2 }}
              onClick={() => {
                setOpen(false);
                if (failure !== 'ticket_expired' && failure !== 'ticket_invalid') onDismiss();
              }}
            >
              {failure === 'own_item'
                ? 'View your item'
                : failure === 'ticket_expired' ||
                    failure === 'ticket_invalid' ||
                    failure === 'vt_unverified'
                  ? 'Close'
                  : 'View this item'}
            </Button>
          )}
        </Stack>
      );
    }

    if (step === 'otp') {
      const { firstName, lastName } = splitName(name);
      return (
        <OnboardingAuthPanel
          email={email}
          companyId={companyId ?? undefined}
          initialCodeSent={codeSent}
          firstName={firstName}
          lastName={lastName}
          onVerified={() => {
            if (ticket) void claimNow(ticket);
          }}
        />
      );
    }

    // details
    if (signedInEmail) {
      return (
        <Stack spacing={1.5}>
          <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.5 }}>
            You are signed in as <strong>{signedInEmail}</strong>. This item will be added to
            that account.
          </Typography>
          {collectLocation && (
            <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.5 }}>
              Your location is recorded with this scan, if your browser allows it.
            </Typography>
          )}
          <Button
            variant='primary'
            className='w-full h-10'
            sx={{ paddingX: 2 }}
            onClick={handleClaimSignedIn}
            disabled={busy || !ticket}
          >
            {busy ? (
              <Loader2 className='w-5 h-5 animate-spin' />
            ) : (
              'Claim ownership'
            )}
          </Button>
        </Stack>
      );
    }

    return (
      <Stack spacing={1.5}>
        <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.5 }}>
          Add your details to make this item yours. We will email you a code to confirm it is
          you.
        </Typography>
        <ContactDetailsFields
          name={name}
          email={email}
          onNameChange={setName}
          onEmailChange={setEmail}
          nameInputRef={nameInputRef}
          emailInputRef={emailInputRef}
          showErrors={showErrors}
          disabled={busy}
          /* Grouped with the other one-tap paths above the divider, NOT after
             Continue (VRG-720) — the same regrouping `RequestAccessInfo` takes,
             and it has to be the same on both or the two gates teach different
             layouts for the identical decision. This drawer is reached from the
             certificate the access gate guards, so a visitor can see both. */
          quickActions={
            <GoogleLoginButton
              label='Continue with Google'
              className='w-full h-10'
              disabled={busy || !ticket}
              companyId={companyId ?? undefined}
              onBeforeRedirect={handleGoogle}
            />
          }
        />
        {collectLocation && (
          <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.5 }}>
            Your location is recorded with this scan, if your browser allows it.
          </Typography>
        )}
        <Button
          variant='primary'
          className='w-full h-10'
          sx={{ paddingX: 2 }}
          onClick={handleContinue}
          disabled={busy || !ticket || !isContactDetailsValid(name, email)}
        >
          {busy ? (
            <Loader2 className='w-5 h-5 animate-spin' />
          ) : (
            'Continue'
          )}
        </Button>
      </Stack>
    );
  })();

  return (
    <>
      <Stack spacing={1.5} sx={gateSurfaceSx}>
        <Stack spacing={0.5}>
          {/* Suppressed in a sheet: DrawerPopup's header already carries this
              title, and rendering it twice is the stacked-duplicate-heading
              defect ScanTag records from the gift flow. */}
          {gateRendersOwnTitle && (
          <Typography variant='h6' sx={{ fontSize: 20, fontWeight: 500 }}>
            {noTicket ? 'Tap to claim' : 'Claim this item'}
          </Typography>
          )}
          <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.5 }}>
            {noTicket
              ? 'To claim this item, tap it with your phone. A fresh tap is what proves it is in your hands.'
              : ownerName
                ? `This belongs to ${ownerName}. Claim it to make it yours.`
                : 'Claim it to add it to your account.'}
          </Typography>
        </Stack>

        {!noTicket && (
          <Button
            variant='primary'
            className='w-full h-10 text-center'
            sx={{ paddingX: 2 }}
            onClick={openDrawer}
            disabled={minting || !ticket}
          >
            {minting ? (
              <Stack direction='row' spacing={1} alignItems='center'>
                <Loader2 className='w-5 h-5 animate-spin' />
                <span>{awaitingSignIn ? 'Finishing sign in' : 'Getting ready'}</span>
              </Stack>
            ) : (
              'Claim ownership'
            )}
          </Button>
        )}

        <Button
          variant='secondary'
          className='w-full h-10 text-center'
          sx={{ paddingX: 2 }}
          onClick={onDismiss}
        >
          View this item
        </Button>
      </Stack>

      {open && (
        <DrawerPopup
          title={step === 'otp' ? 'Confirm your email' : 'Claim ownership'}
          handleOpen={open}
          handleClose={closeDrawer}
          disableBackdropClick={busy}
          disableSwipeToClose={busy}
          headerLeft={
            step === 'otp' ? (
              <IconButton
                aria-label='Back'
                size='small'
                onClick={() => setStep('details')}
              >
                <ArrowLeft size={20} strokeWidth={1.5} />
              </IconButton>
            ) : undefined
          }
          child={
            <Box
              ref={drawerBodyRef}
              sx={{
                minHeight: pinnedHeight ?? undefined,
                display: 'flex',
                flexDirection: 'column',
                // The spinner step centres in the held space rather than hugging the top.
                justifyContent: step === 'working' ? 'center' : 'flex-start',
              }}
            >
              {drawerBody}
            </Box>
          }
        />
      )}
    </>
  );
};
